/**
 * Préférences d'affichage (Profil → Format) : unités, heure, date, devise.
 *
 * La base est en métrique et en CAD ; seule l'interface convertit. Seuls les choix explicites sont
 * enregistrés (navigateur + profil si connecté) ; le reste suit la langue du site et la région du navigateur.
 */

import { useSyncExternalStore } from 'react'
import i18n from '../../i18n'

export const FORMAT_CHOICES = {
  units: ['metric', 'imperial'],
  distance: ['km', 'mi'],
  elevation: ['m', 'ft'],
  temperature: ['c', 'f'],
  weight: ['kg', 'lb'],
  volume: ['l', 'gal'],
  precipitation: ['mm', 'in'],
  area: ['km2', 'ha', 'mi2', 'ac'],
  time_format: ['24h', '12h'],
  date_format: ['dmy', 'mdy', 'ymd'],
  week_start: ['monday', 'sunday'],
  currency: ['CAD', 'USD', 'EUR', 'GBP', 'CHF', 'AUD', 'JPY', 'MXN'],
} as const

type Choices = typeof FORMAT_CHOICES
export type FormatKey = keyof Choices
export type ResolvedFormat = { [K in FormatKey]: Choices[K][number] }
export type FormatPreferences = Partial<ResolvedFormat>
export type UnitSystem = ResolvedFormat['units']
export type Currency = ResolvedFormat['currency']

/** Clés d'unités qui suivent le système (métrique / impérial) tant qu'elles ne sont pas choisies une à une. */
export const UNIT_KEYS = ['distance', 'elevation', 'temperature', 'weight', 'volume', 'precipitation', 'area'] as const
type UnitKey = (typeof UNIT_KEYS)[number]

const SYSTEM_UNITS: Record<UnitSystem, Pick<ResolvedFormat, UnitKey>> = {
  metric: { distance: 'km', elevation: 'm', temperature: 'c', weight: 'kg', volume: 'l', precipitation: 'mm', area: 'km2' },
  imperial: { distance: 'mi', elevation: 'ft', temperature: 'f', weight: 'lb', volume: 'gal', precipitation: 'in', area: 'mi2' },
}

/** Pays où l'on affiche par défaut en impérial / en 12 h / mois avant le jour / semaine commençant le dimanche. */
const IMPERIAL_REGIONS = new Set(['US', 'LR', 'MM'])
const SUNDAY_REGIONS = new Set(['US', 'CA', 'MX', 'BR', 'JP', 'IL', 'PH'])
const CURRENCY_BY_REGION: Record<string, Currency> = {
  US: 'USD', GB: 'GBP', CH: 'CHF', AU: 'AUD', JP: 'JPY', MX: 'MXN',
  FR: 'EUR', BE: 'EUR', DE: 'EUR', ES: 'EUR', IT: 'EUR', NL: 'EUR', AT: 'EUR', IE: 'EUR', PT: 'EUR', LU: 'EUR',
}

const STORAGE_KEY = 'format'
const listeners = new Set<() => void>()
let explicit: FormatPreferences = readStored()
let snapshot: ResolvedFormat | null = null

function readStored(): FormatPreferences {
  try {
    return cleanPreferences(JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'))
  } catch {
    return {}
  }
}

/** Ne garde que les clés et valeurs connues (le reste est ignoré). */
export function cleanPreferences(value: unknown): FormatPreferences {
  if (!value || typeof value !== 'object') return {}
  const out: Record<string, string> = {}
  for (const [key, choice] of Object.entries(value as Record<string, unknown>)) {
    const allowed = (FORMAT_CHOICES as Record<string, readonly string[]>)[key]
    if (allowed && typeof choice === 'string' && allowed.includes(choice)) out[key] = choice
  }
  return out as FormatPreferences
}

const US_TIME_ZONE =
  /^(America\/(New_York|Detroit|Chicago|Denver|Boise|Phoenix|Los_Angeles|Anchorage|Juneau|Sitka|Metlakatla|Yakutat|Nome|Adak|Menominee|Indiana\/.+|Kentucky\/.+|North_Dakota\/.+)|Pacific\/Honolulu|US\/.+)$/
const CA_TIME_ZONE =
  /^(America\/(Toronto|Montreal|Vancouver|Edmonton|Winnipeg|Regina|Swift_Current|Halifax|Glace_Bay|Moncton|Goose_Bay|St_Johns|Whitehorse|Dawson|Dawson_Creek|Fort_Nelson|Creston|Yellowknife|Inuvik|Cambridge_Bay|Rankin_Inlet|Resolute|Iqaluit|Atikokan|Blanc-Sablon|Nipigon|Thunder_Bay|Rainy_River|Pangnirtung)|Canada\/.+)$/

/** Pays d'après le fuseau horaire : la langue du navigateur vaut souvent en-US même au Canada. */
function timeZoneRegion(): string | undefined {
  try {
    const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone
    if (US_TIME_ZONE.test(zone)) return 'US'
    if (CA_TIME_ZONE.test(zone)) return 'CA'
  } catch {
    // Intl indisponible
  }
  return undefined
}

/** Région du navigateur (« US » pour en-US), si la langue correspond à celle du site ; sinon région de la langue du site. */
function browserRegion(siteLanguage: string): string | undefined {
  const candidates = typeof navigator === 'undefined' ? [] : [...(navigator.languages ?? []), navigator.language]
  for (const tag of candidates) {
    if (!tag) continue
    const locale = new Intl.Locale(tag)
    if (locale.language === siteLanguage && locale.region) return locale.region
  }
  return undefined
}

export function defaultFormat(siteLanguage: string = i18n.language || 'fr', region = timeZoneRegion() ?? browserRegion(siteLanguage) ?? 'CA'): ResolvedFormat {
  const units: UnitSystem = IMPERIAL_REGIONS.has(region) ? 'imperial' : 'metric'
  const twelveHour = new Intl.DateTimeFormat(`${siteLanguage}-${region}`, { hour: 'numeric' }).resolvedOptions().hour12
  return {
    units,
    ...SYSTEM_UNITS[units],
    time_format: twelveHour ? '12h' : '24h',
    date_format: region === 'US' ? 'mdy' : siteLanguage === 'en' && region === 'CA' ? 'ymd' : 'dmy',
    week_start: SUNDAY_REGIONS.has(region) && !(siteLanguage === 'fr' && region === 'CA') ? 'sunday' : 'monday',
    currency: CURRENCY_BY_REGION[region] ?? 'CAD',
  }
}

/** Choix explicites + système d'unités + valeurs par défaut. */
export function resolveFormat(prefs: FormatPreferences = explicit, defaults: ResolvedFormat = defaultFormat()): ResolvedFormat {
  const chosen = Object.fromEntries(Object.entries(prefs).filter(([, value]) => value != null))
  const units = prefs.units ?? defaults.units
  const unitDefaults = prefs.units ? SYSTEM_UNITS[units] : {}
  return { ...defaults, units, ...unitDefaults, ...chosen }
}

export function getFormatPreferences(): FormatPreferences {
  return explicit
}

export function getFormat(): ResolvedFormat {
  snapshot ??= resolveFormat()
  return snapshot
}

function notify() {
  snapshot = null
  listeners.forEach((listener) => listener())
}

/** Remplace les choix explicites (valeur vide = revenir au défaut). */
export function setFormatPreferences(next: FormatPreferences) {
  explicit = cleanPreferences(next)
  try {
    if (Object.keys(explicit).length) localStorage.setItem(STORAGE_KEY, JSON.stringify(explicit))
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // stockage indisponible (navigation privée)
  }
  notify()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return
    explicit = readStored()
    notify()
  })
  i18n.on('languageChanged', notify)
}

/** Format résolu ; le composant se met à jour quand les préférences changent. */
export function useFormat(): ResolvedFormat {
  return useSyncExternalStore(subscribe, getFormat, getFormat)
}

export function useFormatPreferences(): FormatPreferences {
  return useSyncExternalStore(subscribe, getFormatPreferences, getFormatPreferences)
}
